import { Component, OnInit } from '@angular/core';
import { FormBuilder, FormControl, FormGroup, Validators } from '@angular/forms';
import { MatDialogRef } from '@angular/material/dialog';
import { catchError, distinctUntilChanged, finalize, map, Observable, of, Subject, switchMap, takeUntil, tap } from 'rxjs';
import { NotificationService } from 'src/app/services/notification.service';
import { helper } from 'src/environments/helper';
import { Generic } from 'src/app/interfaces/generic.interface';
import { DocumentTypesService } from '../document-types.service';
import { AddDocument } from '../document-types.interface';
import { CommonService } from 'src/app/services/common.service';
import { notDefaultValidator } from 'src/app/validators/not-default';
import { UserOrganigramaSearchPayload } from 'src/app/interfaces/common.interface';
import { environment } from 'src/environments/environment';
import { GeneralService } from '../../general/general.service';

@Component({
    selector: 'app-add-document',
    templateUrl: './add-document.component.html',
    styleUrls: ['./add-document.component.scss'],
})
export class AddDocumentComponent {
    // general error
    errorTitle: string = helper.dialogConfig.headers.errorAtentie;
    errorIcon: string = helper.dialogConfig.icons.error;
    errorType: string = helper.dialogConfig.icons.error;
    helper = helper;

    dgaspc_id = environment.interop.dgaspcId;

    isLoading = false;

    serii!: Generic[];
    prioritate$!: Observable<Generic[]>;
    status$!: Observable<Generic[]>;

    directiiActive: any[] = [];
    compartimenteActive!: Generic[];
    usersActive!: Generic[];

    private addDestroy$ = new Subject<void>();

    // Filtered fields for search
    filteredDirectii: any[] = [];
    directiiControl = new FormControl();

    filteredCompartimente: any[] = [];
    compartimenteControl = new FormControl();

    filteredUsers: any[] = [];
    usersControl = new FormControl();

    filteredSerii: any[] = [];
    seriiControl = new FormControl();

    defaultParameter: { name: string; id: number } = {
        name: '--Alegeți--',
        id: -1,
    };

    form: FormGroup = new FormGroup({
        name: new FormControl('', [Validators.required]),
        id_serie: new FormControl(-1, [Validators.required, notDefaultValidator('-1')]),
        id_priority: new FormControl(-1, [Validators.required, notDefaultValidator('-1')]),
        id_status: new FormControl(-1, [Validators.required, notDefaultValidator('-1')]),
        id_directie: new FormControl(this.dgaspc_id, [Validators.required, notDefaultValidator('-1')]),
        id_compartiment: new FormControl(-1, []),
        id_user: new FormControl(-1, []),
        observatii: new FormControl('', []),
    });

    constructor(
        private dialogRef: MatDialogRef<AddDocumentComponent>,
        public requestService: DocumentTypesService,
        private notificationService: NotificationService,
        private commonService: CommonService,
        private generalService: GeneralService
    ) { }

    closeDialog() {
        this.dialogRef?.close({
            success: false,
        });
    }

    ngOnInit(): void {
        this.getActiveSerie();
        this.getActivePriorities();
        this.getActiveStatus();

        this.getActiveDirectii();
        this.loadCompartimenteActive(this.dgaspc_id);
        this.renderFieldsSearch();
        this.loadData();
    }

    ngOnDestroy(): void {
        this.addDestroy$.next();
        this.addDestroy$.complete();
    }

    renderFieldsSearch() {
        this.directiiControl.valueChanges.pipe(
            takeUntil(this.addDestroy$),
            distinctUntilChanged(),
            switchMap((inputValue) => {
                if (inputValue.length < 1) {
                    this.filteredDirectii = this.directiiActive;
                    return of(this.directiiActive);
                }
                return this.filterDirectii(inputValue);
            }),
            tap((data: any[]) => {
                this.filteredDirectii = data;
            }),
            catchError(() => of([]))
        ).subscribe();

        this.compartimenteControl.valueChanges.pipe(
            takeUntil(this.addDestroy$),
            distinctUntilChanged(),
            switchMap((inputValue) => {
                if (inputValue.length < 1) {
                    this.filteredCompartimente = this.compartimenteActive;
                    return of(this.compartimenteActive);
                }
                return this.filterCompartimente(inputValue);
            }),
            tap((data: any[]) => {
                this.filteredCompartimente = data;
            }),
            catchError(() => of([]))
        ).subscribe();

        this.usersControl.valueChanges.pipe(
            takeUntil(this.addDestroy$),
            distinctUntilChanged(),
            switchMap((inputValue) => {
                if (inputValue.length < 1) {
                    this.filteredUsers = this.usersActive;
                    return of(this.usersActive);
                }
                return this.filterUsers(inputValue);
            }),
            tap((data: any[]) => {
                this.filteredUsers = data;
            }),
            catchError(() => of([]))
        ).subscribe();

        this.seriiControl.valueChanges.pipe(
            takeUntil(this.addDestroy$),
            distinctUntilChanged(),
            switchMap((inputValue) => {
                if (inputValue.length < 1) {
                    this.filteredSerii = this.serii;
                    return of(this.serii);
                }
                return this.filterSerii(inputValue);
            }),
            tap((data: any[]) => {
                this.filteredSerii = data;
            }),
            catchError(() => of([]))
        ).subscribe();
    }

    getActiveSerie() {
        this.isLoading = true;
        this.commonService.getSeriiActive()
            .pipe(
                finalize(() => (this.isLoading = false)),
            )
            .subscribe({
                next: (res: any) => {
                    this.serii = res;
                    this.filteredSerii = [...this.serii];
                    return;
                },
                error: (err: any) => {
                    console.log(err.error);
                }
            })
    }

    filterSerii(searchTerm: string): Observable<any[]> {
        const normalizedSearchTerm = this.normalizeString(searchTerm);

        const filtered = this.serii.filter(serie =>
            this.normalizeString(serie.name).includes(normalizedSearchTerm)
        );

        return of(filtered);
    }

    onSerieSelectClosed() {
        const selectedValue = this.seriiControl.value;

        if (selectedValue) {
            const isSelectedInFilteredList = this.filteredSerii.some(serie =>
                serie.id === this.form.get('id_serie')?.value
            );

            if (!isSelectedInFilteredList) {
                this.seriiControl.setValue(null);
                this.filteredSerii = this.serii;
                this.addDestroy$.next();
                this.addDestroy$.complete();
                this.renderFieldsSearch();
            }
        }
    }

    getActivePriorities() {
        this.isLoading = true;
        this.prioritate$ = this.commonService
            .getPrioritiesActive()
            .pipe(
                finalize(() => (this.isLoading = false)),
                map((resp: Generic[]) => [this.defaultParameter, ...resp])
            );
    }

    getActiveStatus() {
        this.isLoading = true;
        this.status$ = this.commonService
            .getStatusesActive()
            .pipe(
                finalize(() => (this.isLoading = false)),
                map((resp: Generic[]) => [this.defaultParameter, ...resp])
            );
    }

    normalizeString(value: string): string {
        return value.normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLowerCase();
    }

    filterDirectii(searchTerm: string): Observable<any[]> {
        const normalizedSearchTerm = this.normalizeString(searchTerm);

        const filtered = this.directiiActive.filter(directie =>
            this.normalizeString(directie.name).includes(normalizedSearchTerm)
        );

        return of(filtered);
    }

    getActiveDirectii() {
        this.isLoading = true;
        this.commonService.getDirectiiActive()
            .pipe(
                finalize(() => (this.isLoading = false)),
            )
            .subscribe({
                next: (res: any) => {
                    this.directiiActive = res;
                    this.filteredDirectii = [...this.directiiActive];
                    return;
                },
                error: (err: any) => {
                    console.log(err.error);
                }
            })
    }

    onDirectieSelectClosed() {
        const selectedValue = this.directiiControl.value;

        if (selectedValue) {
            const isSelectedInFilteredList = this.filteredDirectii.some(directie =>
                directie.id === this.form.get('id_directie')?.value
            );

            if (!isSelectedInFilteredList) {
                this.directiiControl.setValue(null);
                this.filteredDirectii = this.directiiActive;
                this.addDestroy$.next();
                this.addDestroy$.complete();
                this.renderFieldsSearch();
            }
        }
    }

    onDirectieChange(directieId: number): void {
        this.form.controls['id_compartiment'].patchValue(-1);
        this.form.controls['id_user'].patchValue(-1);

        if (directieId == -1) {
            this.compartimenteActive = [];
            this.filteredCompartimente = [];
            this.usersActive = [];
            this.filteredUsers = [];
            return;
        }

        if (directieId) {
            this.loadCompartimenteActive(directieId);
            this.loadUsers();
        }
    }

    loadCompartimenteActive(directieId?: number) {
        this.isLoading = true;
        let compartimenteObservable$ = directieId
            ? this.commonService.getCompartimenteByDirectieId(directieId)
            : this.commonService.getCompartimenteActive();

        compartimenteObservable$.pipe(
            map((results: any) => [...results]),
            finalize(() => (this.isLoading = false))
        ).subscribe({
            next: (res) => {
                this.compartimenteActive = [...res];
                this.filteredCompartimente = [...res];
            },
            error: (err) => {
                console.log(err);
                this.filteredCompartimente = [];
            },
        })
    }

    filterCompartimente(searchTerm: string): Observable<any[]> {
        const normalizedSearchTerm = this.normalizeString(searchTerm);

        const filtered = this.compartimenteActive.filter(compartiment =>
            this.normalizeString(compartiment.name).includes(normalizedSearchTerm)
        );

        return of(filtered);
    }

    onCompartimentSelectClosed() {
        const selectedValue = this.compartimenteControl.value;

        if (selectedValue) {
            const isSelectedInFilteredList = this.filteredCompartimente.some(compartiment =>
                compartiment.id === this.form.get('id_compartiment')?.value
            );

            if (!isSelectedInFilteredList) {
                this.compartimenteControl.setValue(null);
                this.filteredCompartimente = this.compartimenteActive;
                this.addDestroy$.next();
                this.addDestroy$.complete();
                this.renderFieldsSearch();
            }
        }
    }

    onCompartimentChange(compartimentId: number) {
        this.form.controls['id_user'].patchValue(-1);

        if (compartimentId) {
            this.loadUsers();
        }
    }

    loadUsers() {
        this.isLoading = true;

        const payload: UserOrganigramaSearchPayload = {
            directie_id: this.form.get('id_directie')?.value,
            compartiment_id: this.form.get('id_compartiment')?.value !== -1 ? this.form.get('id_compartiment')?.value : null,
        };

        this.commonService
            .getUsersByOrganigrama(payload)
            .pipe(
                map((results: any) => [...results]),
                finalize(() => (this.isLoading = false))
            ).subscribe({
                next: (res) => {
                    this.usersActive = [...res];
                    this.filteredUsers = [...res];
                },
                error: (err) => {
                    console.log(err);
                    this.filteredUsers = [];
                },
            });
    }

    filterUsers(searchTerm: string): Observable<Generic[]> {
        const normalizedSearchTerm = this.normalizeString(searchTerm);

        return of(this.usersActive.filter(user =>
            this.normalizeString(user.name).includes(normalizedSearchTerm)
        ));
    }

    onUsersSelectClosed() {
        const selectedValue = this.usersControl.value;

        if (selectedValue) {
            const isSelectedInFilteredList = this.filteredUsers.some(user =>
                user.id === this.form.get('id_user')?.value
            );

            if (!isSelectedInFilteredList) {
                this.usersControl.setValue(null);
                this.filteredUsers = this.usersActive;
                this.addDestroy$.next();
                this.addDestroy$.complete();
                this.renderFieldsSearch();
            }
        }
    }

    private loadData(): void {
        this.isLoading = true;
        this.generalService.list().subscribe({
            next: (response: any) => {
                if (response.errors === false && response.data) {
                    const defaultPriority = response.data.find((item: any) => item.name === 'default_priority');
                    const defaultStatus = response.data.find((item: any) => item.name === 'default_status');

                    if (defaultPriority) {
                        this.form.get('id_priority')?.patchValue(parseInt(defaultPriority.value));
                    }

                    if (defaultStatus) {
                        this.form.get('id_status')?.patchValue(parseInt(defaultStatus.value));
                    }
                }
                this.isLoading = false;
            },
            error: (error) => {
                console.error('Error loading data:', error);
                this.isLoading = false;
            }
        });
    }

    submitForm() {
        this.isLoading = true;

        const payload: AddDocument = {
            name: this.form.get('name')?.value,
            id_serie: this.form.get('id_serie')?.value,
            id_status: this.form.get('id_status')?.value,
            id_priority: this.form.get('id_priority')?.value,
            id_directie: this.form.get('id_directie')?.value,
            id_compartiment: this.form.get('id_compartiment')?.value,
            id_user: this.form.get('id_user')?.value,
            observatii: this.form.get('observatii')?.value,
        };

        this.requestService
            .storeEntry(payload)
            .pipe(finalize(() => (this.isLoading = false)))
            .subscribe({
                next: (res) => {
                    this.dialogRef.close({ success: true });
                },
                error: (err) => {
                    let msg;
                    try {
                        // try to catch the message from server
                        msg = err.error.errors.message[0];
                    } catch (error) {
                        // controll output message
                        msg = helper.dialogConfig.generalMessages.error;
                    }
                    this.notificationService.warningSwal(
                        this.errorTitle,
                        msg,
                        this.errorIcon
                    );
                },
            });
    }
}
